import { UserRole } from 'stageclip-common';

import { appStore } from '../store/appStore';
import { getItem } from '../store/selectors';

interface Permissions {
  [restrictedAction: string]: UserRole[];
}

export enum RestrictedActions {
  AccessPortals,
  GetUsers,
  GetClients,
  CreateUser,
  CreateClient,
  CreateEvent,
  CreatePortal,
  UploadParticipant,
  UploadAegisubResult,
  AccessUsersPage,
  AccessClientsPage,
  AddEventVideo,
  EditEvent,
  AccessAdminSection,
  ViewEventPrice,
  ViewParticipantsUploadDate,
  ViewEventStorageDetails,
  AccessShops,
  MonitoringPage,
  MonitoringVGPage,
  EditClient,
  ViewEventStatistics,
  ChangeOtherUsersPassword,
  EditParticipant,
  SendParticipantsToGenerator,
  SendLiveParticipantsToGenerator,
  SendParticipantsToRRE,
  SendLiveParticipantsToRRE,
  AccessAegisubAudioToVideoConverter,
  AssignEvent,
  ApplyTransition,
  GenerateParticipantsZip,
  DownloadParticipantsZip,
  DeleteParticipantsZip,
  DeleteParticipantsData,
  AccessClientsUsersPage,
  UpdateClipTemplateConfiguration,
  GenerateParticipantsVgZip,
  DownloadParticipantsVgZip,
  DeleteParticipantsVgZip,
  GenerateVgVideoCeremony,
  AddCheckoutData,
  EditCheckoutDetails,
  DeleteUser = 'DeleteUser',
  GetMoEvents = 'GetMoEvents',
  VgQaQueue = 'VgQaQueue',
  SendVideoReminder = 'SendVideoReminder',
  ViewMailerLog = 'ViewMailerLog',
  CancelEvent = 'CancelEvent',
  AccessClipGenerator = 'AccessClipGenerator',
}

const permissions: Permissions = {
  [RestrictedActions.AccessPortals]: [UserRole.Admin],
  [RestrictedActions.AccessAdminSection]: [UserRole.Admin],
  [RestrictedActions.AccessClientsPage]: [UserRole.Admin, UserRole.ReadOnly],
  [RestrictedActions.AccessShops]: [UserRole.Admin],
  [RestrictedActions.AccessUsersPage]: [UserRole.Admin],
  [RestrictedActions.AccessClipGenerator]: [UserRole.Admin],
  [RestrictedActions.AddEventVideo]: [UserRole.Admin],
  [RestrictedActions.AccessAegisubAudioToVideoConverter]: [UserRole.Admin, UserRole.Clipper, UserRole.LimitedClipper],
  [RestrictedActions.CreateClient]: [UserRole.Admin],
  [RestrictedActions.CreateEvent]: [UserRole.Admin],
  [RestrictedActions.CreateUser]: [UserRole.Admin],
  [RestrictedActions.CreatePortal]: [UserRole.Admin],
  [RestrictedActions.EditEvent]: [UserRole.Admin],
  [RestrictedActions.GetClients]: [UserRole.Admin, UserRole.ReadOnly],
  [RestrictedActions.GetUsers]: [UserRole.Admin],
  [RestrictedActions.MonitoringPage]: [UserRole.Admin, UserRole.ReadOnly],
  [RestrictedActions.MonitoringVGPage]: [UserRole.Admin, UserRole.ReadOnly],
  [RestrictedActions.UploadParticipant]: [UserRole.Admin],
  [RestrictedActions.UploadAegisubResult]: [UserRole.Admin, UserRole.Clipper, UserRole.LimitedClipper],
  [RestrictedActions.ViewEventPrice]: [UserRole.Admin, UserRole.ReadOnly],
  [RestrictedActions.ViewParticipantsUploadDate]: [UserRole.Admin, UserRole.ReadOnly],
  [RestrictedActions.ViewEventStorageDetails]: [UserRole.Admin],
  [RestrictedActions.EditClient]: [UserRole.Admin],
  [RestrictedActions.ViewEventStatistics]: [UserRole.Admin, UserRole.ReadOnly],
  [RestrictedActions.ChangeOtherUsersPassword]: [UserRole.Admin],
  [RestrictedActions.EditParticipant]: [UserRole.Admin, UserRole.Clipper, UserRole.LimitedClipper],
  [RestrictedActions.SendParticipantsToGenerator]: [UserRole.Admin],
  [RestrictedActions.SendLiveParticipantsToGenerator]: [UserRole.Admin],
  [RestrictedActions.AssignEvent]: [UserRole.Admin, UserRole.Clipper, UserRole.LimitedClipper],
  [RestrictedActions.ApplyTransition]: [UserRole.Admin, UserRole.Clipper, UserRole.LimitedClipper],
  [RestrictedActions.GenerateParticipantsZip]: [UserRole.Admin],
  [RestrictedActions.DownloadParticipantsZip]: [UserRole.Admin],
  [RestrictedActions.DeleteParticipantsZip]: [UserRole.Admin],
  [RestrictedActions.GenerateParticipantsVgZip]: [UserRole.Admin],
  [RestrictedActions.DownloadParticipantsVgZip]: [UserRole.Admin],
  [RestrictedActions.DeleteParticipantsVgZip]: [UserRole.Admin],
  [RestrictedActions.GenerateVgVideoCeremony]: [UserRole.Admin],
  [RestrictedActions.DeleteParticipantsData]: [UserRole.Admin],
  [RestrictedActions.AccessClientsUsersPage]: [UserRole.Admin],
  [RestrictedActions.UpdateClipTemplateConfiguration]: [UserRole.Admin],
  [RestrictedActions.GetMoEvents]: [UserRole.Admin],
  [RestrictedActions.VgQaQueue]: [UserRole.Admin, UserRole.Clipper],
  [RestrictedActions.SendVideoReminder]: [UserRole.Admin],
  [RestrictedActions.DeleteUser]: [UserRole.Admin],
  [RestrictedActions.ViewMailerLog]: [UserRole.Admin],
  [RestrictedActions.CancelEvent]: [UserRole.Admin],
  [RestrictedActions.AddCheckoutData]: [UserRole.Admin],
  [RestrictedActions.EditCheckoutDetails]: [UserRole.Admin],
  [RestrictedActions.SendParticipantsToRRE]: [UserRole.Admin],
  [RestrictedActions.SendLiveParticipantsToRRE]: [UserRole.Admin],
};

export function userCan(action: RestrictedActions): boolean {
  const currentUser = getItem(appStore.getState().entities.users, appStore.getState().appStatus.session.user);

  return permissions[action].find(role => role === currentUser.role) !== undefined;
}
